import { useState, useEffect, useRef, useCallback } from 'react';

// Full-screen faithful slide player. Reads a manifest of slide images + video
// overlays (each video positioned by % over its slide) and plays them inline.
// baseUrl is where the media lives, e.g. "/presentations/cpr-coating/".
export default function PresentationPlayer({ baseUrl, onClose }) {
  const [manifest, setManifest] = useState(null);
  const [i, setI] = useState(0);
  const [err, setErr] = useState(null);
  const touchX = useRef(null);

  useEffect(() => {
    fetch(baseUrl + 'manifest.json')
      .then(r => r.json())
      .then(setManifest)
      .catch(() => setErr('Could not load this presentation. The files may not be uploaded yet.'));
  }, [baseUrl]);

  const total = manifest ? manifest.slides.length : 0;
  const go = useCallback((d) => setI(v => Math.min(Math.max(v + d, 0), total - 1)), [total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') { e.preventDefault(); go(1); }
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); go(-1); }
      else if (e.key === 'Escape') onClose && onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  // pause any playing videos when leaving a slide
  useEffect(() => {
    const vids = document.querySelectorAll('.pp-stage video');
    vids.forEach(v => { try { v.pause(); } catch (e) {} });
  }, [i]);

  if (err) return (
    <div style={overlay}>
      <button onClick={onClose} style={closeBtn}>✕</button>
      <div style={{ color: '#fff', textAlign: 'center', maxWidth: 420, padding: 20 }}>
        <div style={{ fontSize: 40, marginBottom: 10 }}>⚠️</div>{err}
      </div>
    </div>
  );
  if (!manifest) return <div style={overlay}><div style={{ color: '#fff' }}>Loading presentation…</div></div>;

  const slide = manifest.slides[i];

  return (
    <div style={overlay}
      onTouchStart={e => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={e => { if (touchX.current == null) return; const dx = e.changedTouches[0].clientX - touchX.current; if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1); touchX.current = null; }}>

      <button onClick={onClose} style={closeBtn} title="Close (Esc)">✕</button>

      <div className="pp-stage" style={stage}>
        <div style={{ position: 'relative', width: '100%', maxWidth: 'calc((100vh - 90px) * 1.7778)' }}>
          {/* slide image sets the aspect box */}
          <img src={baseUrl + slide.image} alt={`Slide ${i + 1}`} style={{ width: '100%', display: 'block', borderRadius: 6 }} draggable={false} />
          {/* video overlays positioned exactly over the slide */}
          {(slide.videos || []).map((v, k) => (
            <video key={k} src={baseUrl + v.src} controls playsInline preload="none"
              style={{ position: 'absolute', left: v.x + '%', top: v.y + '%', width: v.w + '%', height: v.h + '%', objectFit: 'cover', background: '#000', borderRadius: 2 }} />
          ))}
        </div>
      </div>

      {/* controls */}
      <button onClick={() => go(-1)} disabled={i === 0} style={{ ...arrow, left: 14, opacity: i === 0 ? 0.3 : 1 }}>‹</button>
      <button onClick={() => go(1)} disabled={i === total - 1} style={{ ...arrow, right: 14, opacity: i === total - 1 ? 0.3 : 1 }}>›</button>

      <div style={bottomBar}>
        <span style={{ fontWeight: 700 }}>{manifest.title}</span>
        <span style={{ opacity: 0.8 }}>Slide {i + 1} / {total}</span>
      </div>

      {/* progress dots */}
      <div style={dots}>
        {manifest.slides.map((_, k) => (
          <span key={k} onClick={() => setI(k)}
            style={{ width: k === i ? 22 : 8, height: 8, borderRadius: 8, background: k === i ? '#e3b341' : 'rgba(255,255,255,.45)', cursor: 'pointer', transition: 'width .2s' }} />
        ))}
      </div>
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, background: '#0b0b0b', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' };
const stage = { flex: 1, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 60px 70px' };
const closeBtn = { position: 'absolute', top: 14, right: 16, zIndex: 3, width: 40, height: 40, borderRadius: '50%', border: 0, background: 'rgba(255,255,255,.15)', color: '#fff', fontSize: 18, cursor: 'pointer' };
const arrow = { position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: 50, height: 50, borderRadius: '50%', border: 0, background: 'rgba(255,255,255,.18)', color: '#fff', fontSize: 30, cursor: 'pointer', zIndex: 3 };
const bottomBar = { position: 'absolute', bottom: 16, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', padding: '0 24px', color: '#fff', fontSize: 13 };
const dots = { position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 5, alignItems: 'center', maxWidth: '70%', flexWrap: 'wrap', justifyContent: 'center' };
